/**
 * Read-only consumer of Horizon 4.2 ProductSelectEvent.
 * Native components continue to own selection, prices, payment and cart state.
 */
(() => {
  const ticketSelector = '.product-details[data-tiger-purchase-style="compact_field_ticket"]';
  const fields = ['meta', 'summary', 'saving', 'trust', 'rating'];
  const requests = new WeakMap();

  // Judge.me may hydrate later than the section. A store badge is never a
  // product star rating; hide the fallback once a real product review exists.
  function syncRating(ticket) {
    const badge = ticket.querySelector('[data-tiger-ticket-rating]');
    if (!badge) return;
    const review = ticket.querySelector('.jdgm-prev-badge[data-number-of-reviews]');
    if (review) {
      const count = Number(review.getAttribute('data-number-of-reviews'));
      if (Number.isFinite(count)) badge.hidden = count > 0 || !badge.textContent.trim();
    }
  }

  function observeRatings(root = document) {
    root.querySelectorAll(ticketSelector).forEach((ticket) => {
      if (ticket.dataset.tigerRatingObserved) return;
      ticket.dataset.tigerRatingObserved = 'true';
      syncRating(ticket);
      // Observe the app's count/children, not the hidden attribute we set.
      const observer = new MutationObserver(() => syncRating(ticket));
      observer.observe(ticket, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-number-of-reviews'] });
      ticket.addEventListener('tiger:dispose', () => observer.disconnect(), { once: true });
    });
  }

  document.addEventListener('shopify:product:select', (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;
    const section = event.target.closest('.shopify-section');
    const ticket = section?.querySelector(ticketSelector);
    if (!section || !ticket) return;
    const request = {};
    requests.set(section, request);
    event.promise.then(({ detail }) => {
      // A slower earlier request must never overwrite the latest option.
      if (requests.get(section) !== request || !section.isConnected || !detail?.html) return;
      const nextSection = detail.html.getElementById(section.id);
      // Fail closed if this is not the same section; never read another product.
      if (!nextSection) return;
      const currentTicket = section.querySelector(ticketSelector);
      const nextTicket = nextSection.querySelector(ticketSelector);
      if (!currentTicket || !nextTicket) return;
      for (const field of fields) {
        const selector = `[data-tiger-ticket-${field}]`;
        const current = currentTicket.querySelector(selector);
        if (!current) continue;
        const next = nextTicket.querySelector(selector);
        if (next) current.replaceWith(next.cloneNode(true));
        else {
          // Retain a stable anchor but clear stale variant-specific content.
          current.replaceChildren();
          current.hidden = true;
        }
      }
      syncRating(currentTicket);
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[Tiger ticket] Could not refresh product details.', error);
    });
  });

  document.addEventListener('shopify:section:load', () => observeRatings());
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element) event.target.querySelectorAll(ticketSelector).forEach((ticket) => ticket.dispatchEvent(new Event('tiger:dispose')));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => observeRatings(), { once: true });
  else observeRatings();
})();
